/**
 * @module features/auth/hooks/useAuth
 * @description Authentication context & hook managing session state, in-memory tokens,
 * silent session renewal on mount (supporting both Super Admin and Client Portal sessions),
 * dynamic Client Organization `theme_color` application, and automatic logout upon refresh failure.
 */

import * as React from "react";
import { UserDto } from "@/types/auth";
import {
  refreshSession,
  logoutUser,
  clientRefreshSession,
  clientLogoutUser,
} from "../api/auth-api";
import { setOnAuthFailure, setAccessToken } from "@/lib/api-client";
import { applyClientThemeColor } from "@/features/clients/types";

export interface AuthContextType {
  user: UserDto | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: UserDto) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

const SESSION_HINT_KEY = "rx_auth_session";
const PORTAL_HINT_KEY = "rx_auth_portal";
let inflightRefreshPromise: Promise<{ access_token: string; user: UserDto }> | null = null;

function syncUserThemeAndPortalHint(userObj: UserDto): void {
  try {
    localStorage.setItem(SESSION_HINT_KEY, "1");
    localStorage.setItem(
      PORTAL_HINT_KEY,
      userObj.role === "client" ? "client" : "super_admin"
    );
    if (userObj.client?.theme_color) {
      applyClientThemeColor(userObj.client.theme_color);
    }
  } catch {
    // Ignore localStorage issues in restricted contexts
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = React.useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  const login = React.useCallback((newUser: UserDto) => {
    syncUserThemeAndPortalHint(newUser);
    setUser(newUser);
  }, []);

  const logout = React.useCallback(async () => {
    const isClientSession =
      user?.role === "client" ||
      (typeof localStorage !== "undefined" &&
        localStorage.getItem(PORTAL_HINT_KEY) === "client");
    try {
      if (isClientSession) {
        await clientLogoutUser();
      } else {
        await logoutUser();
      }
    } catch (err) {
      console.error("Logout request failed:", err);
    } finally {
      try {
        localStorage.removeItem(SESSION_HINT_KEY);
        localStorage.removeItem(PORTAL_HINT_KEY);
      } catch {}
      setAccessToken(null);
      setUser(null);
    }
  }, [user?.role]);

  const refresh = React.useCallback(async () => {
    try {
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : "";
      const portalHint =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(PORTAL_HINT_KEY)
          : null;
      const useClientRefresh =
        pathname.startsWith("/client") || portalHint === "client";

      if (!inflightRefreshPromise) {
        inflightRefreshPromise = useClientRefresh
          ? clientRefreshSession()
          : refreshSession();
      }
      const data = await inflightRefreshPromise;
      syncUserThemeAndPortalHint(data.user);
      setUser(data.user);
    } catch (err) {
      try {
        localStorage.removeItem(SESSION_HINT_KEY);
        localStorage.removeItem(PORTAL_HINT_KEY);
      } catch {}
      setAccessToken(null);
      setUser(null);
      throw err;
    } finally {
      inflightRefreshPromise = null;
    }
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    setOnAuthFailure(() => {
      try {
        localStorage.removeItem(SESSION_HINT_KEY);
        localStorage.removeItem(PORTAL_HINT_KEY);
      } catch {}
      if (isMounted) {
        setUser(null);
      }
    });

    const initAuth = async () => {
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : "";
      let hasSessionHint = false;
      let portalHint: string | null = null;
      try {
        hasSessionHint = localStorage.getItem(SESSION_HINT_KEY) === "1";
        portalHint = localStorage.getItem(PORTAL_HINT_KEY);
      } catch {}

      const isSuperAdminProtectedRoute =
        pathname.startsWith("/s-admin") && pathname !== "/s-admin/login";
      const isClientProtectedRoute = pathname.startsWith("/client");
      const isProtectedRoute =
        isSuperAdminProtectedRoute || isClientProtectedRoute;

      if (!hasSessionHint && !isProtectedRoute) {
        if (isMounted) {
          setUser(null);
          setIsLoading(false);
        }
        return;
      }

      const useClientRefresh =
        isClientProtectedRoute ||
        (!isSuperAdminProtectedRoute && portalHint === "client");

      try {
        if (!inflightRefreshPromise) {
          inflightRefreshPromise = useClientRefresh
            ? clientRefreshSession()
            : refreshSession();
        }
        const data = await inflightRefreshPromise;
        syncUserThemeAndPortalHint(data.user);
        if (isMounted) {
          setUser(data.user);
        }
      } catch {
        try {
          localStorage.removeItem(SESSION_HINT_KEY);
          localStorage.removeItem(PORTAL_HINT_KEY);
        } catch {}
        if (isMounted) {
          setUser(null);
        }
      } finally {
        inflightRefreshPromise = null;
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = React.useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      refresh,
    }),
    [user, isLoading, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
